import styles from './index.module.css'
import dayjs from 'dayjs'
import React from 'react'
import { useSelector } from 'hooks/use-redux'
import { getCampaign } from '@disco/state-management/selectors/campaigns'
import { plural } from '@disco/utils/string-utils'
import type { TimeRemaining } from '@disco/utils/time-utils'
import '@disco/utils/time-utils'
import Flex from 'components/ui/flex'
import Icon from 'components/ui/icon'
import Text from 'components/ui/text'
import Tooltip from 'components/ui/tooltip'
import { useLiveCountdown } from 'hooks/use-live-countdown'

const getCaption = (timeRemaining: TimeRemaining) => {
  let caption = ''
  if (!timeRemaining) return caption

  if (timeRemaining.days > 0) {
    caption += ` ${plural(timeRemaining.days, 'day', 'days')}`
  }

  if (timeRemaining.hours) {
    caption += ` ${timeRemaining.hours}h`
  }

  if (timeRemaining.minutes) {
    caption += ` ${timeRemaining.minutes}m`
  }

  return `Sending in ${caption}`
}

const LiveCountdown = ({
  campaignId,
}: {
  campaignId: string
}): React.ReactNode => {
  const campaign = useSelector(state =>
    getCampaign(state, {
      campaignId: Number(campaignId),
    }),
  )
  const date = dayjs.tz(
    dayjs(campaign.scheduled_at),
    campaign.scheduled_at_timezone,
  )
  const timeRemaining = useLiveCountdown({
    date: date.format('YYYY-MM-DD'),
    time: date.format('HH:mm'),
    timeZone: campaign.scheduled_at_timezone,
  })
  const tooltipContent = `${date.format('MMM D, YYYY')}, at ${date.format(
    'hh:mm A',
  )} (GMT${date.format('Z')})`
  const caption = getCaption(timeRemaining)
  const lessThanHour = timeRemaining?.days === 0 && timeRemaining?.hours === 0
  if (!caption) return null
  return (
    <Tooltip content={`Scheduled for ${tooltipContent}`}>
      <div className={lessThanHour ? styles.warning : ''}>
        <Flex spacing="small">
          <Icon type="email-scheduled" />
          <Text type="paragraph">{caption}</Text>
        </Flex>
      </div>
    </Tooltip>
  )
}

export default LiveCountdown
